/**
 * Mock data for Booking Widget Embed
 *
 * Provides realistic sample data for all booking steps.
 * This allows full design iteration without any backend dependency.
 * Will be replaced with real API calls in Phase 3.
 */

import type { TimeSlot, PlayerInfo, BookingAccess } from './types'

// ─── Club Info ───────────────────────────────────────────────────────────────

export const mockClub = {
  id: 'mock-club-001',
  name: 'Royal Melbourne Golf Club',
  address: '380 Cheltenham Rd, Black Rock VIC 3193',
  phone: '+61 3 9598 6755',
  email: 'visitors@royalmelbourne.com.au',
  website: 'https://www.royalmelbourne.com.au',
  logoUrl: '/placeholder-club-logo.png',
  profileUrl: null as string | null,
  timezone: 'Australia/Melbourne',
}

export const mock7MBClub = {
  id: '7mb',
  name: '7 Mile Beach Golf Club',
  address: 'Crooked River Rd, Gerroa NSW 2534',
  phone: '+61 2 4234 1585',
  email: 'info@7milegolf.com.au',
  website: 'https://www.7milegolf.com.au',
  logoUrl: 'https://www.7milegolf.com.au/wp-content/uploads/2025/07/7MB-Monotone-DBG.png',
  profileUrl: null as string | null,
  timezone: 'Australia/Sydney',
}

// ─── Courses ─────────────────────────────────────────────────────────────────

export interface MockCourse {
  id: string
  name: string
  holes: number
  par: number
  description: string
  comingSoon?: boolean
}

export const mockCourses: MockCourse[] = [
  {
    id: 'course-west',
    name: 'West Course',
    holes: 18,
    par: 72,
    description: 'Championship course designed by Alister MacKenzie',
  },
  {
    id: 'course-east',
    name: 'East Course',
    holes: 18,
    par: 72,
    description: 'Classic layout with stunning views of Port Phillip Bay',
  },
  {
    id: 'course-composite',
    name: 'Composite Course',
    holes: 18,
    par: 72,
    description: 'The famous composite layout used for major championships',
  },
]

export const mock7MBCourses: MockCourse[] = [
  {
    id: 'course-7mb-south',
    name: 'South Course',
    holes: 18,
    par: 72,
    description: 'Stunning coastal links course with ocean views along the South Coast',
  },
  {
    id: 'course-7mb-north',
    name: 'North Course',
    holes: 18,
    par: 72,
    description: 'New championship layout — coming soon',
    comingSoon: true,
  },
]

// ─── Tee Times ───────────────────────────────────────────────────────────────

/**
 * Seeded pseudo-random number generator for deterministic tee time data.
 * Uses a simple mulberry32 algorithm so the same date always produces the same slots.
 */
function seededRandom(seed: number): () => number {
  let s = seed | 0
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function generateTeeTimes(date: Date, courseId: string, courseName: string): TimeSlot[] {
  const slots: TimeSlot[] = []

  // No tee times on Mondays (day 1)
  if (date.getDay() === 1) return slots

  // Deterministic seed from date + courseId so same date always shows same data
  const dateSeed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate()
  const courseHash = courseId.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  const rand = seededRandom(dateSeed + courseHash)

  // 7:30am to 2:00pm, every 15 minutes
  const startMinutes = 7 * 60 + 30  // 7:30
  const endMinutes = 14 * 60         // 14:00

  for (let mins = startMinutes; mins <= endMinutes; mins += 15) {
    const hour = Math.floor(mins / 60)
    const minute = mins % 60
    const time = new Date(date)
    time.setHours(hour, minute, 0, 0)

    // ~50% of tee times are sold out
    const isSold = rand() < 0.5
    // Most available tee times have 4 slots; occasionally 2-3
    const remainingSlots = isSold ? 0 : (rand() < 0.75 ? 4 : (rand() < 0.5 ? 3 : 2))

    slots.push({
      id: `tt-${courseId}-${hour}-${minute}`,
      time: time.toISOString(),
      available: !isSold && remainingSlots > 0,
      perAmount: 1,
      domesticPrice: 35000,
      internationalPrice: 45000,
      maxPlayers: 4,
      remainingSlots,
      courseId,
      courseName,
      clubNote: undefined,
      reciprocalPricingEnabled: true,
      homeClubRequired: true,
      bookingAccess: 'PUBLIC' as BookingAccess,
    })
  }

  return slots
}

// ─── Available Dates ─────────────────────────────────────────────────────────

export function getMockAvailableDates(): Date[] {
  const dates: Date[] = []
  const today = new Date()

  for (let i = 1; i <= 90; i++) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    const day = date.getDay()

    // Available every day except Mondays (day 1)
    if (day !== 1) {
      dates.push(date)
    }
  }

  return dates
}

// ─── Get Tee Times for Date ──────────────────────────────────────────────────

const allCourses = [...mockCourses, ...mock7MBCourses]

export function getMockTeeTimes(date: Date, courseId?: string, clubCourses?: MockCourse[]): TimeSlot[] {
  // Use club-specific courses if provided, otherwise all courses
  const coursesToSearch = clubCourses || allCourses
  const selectableCourses = coursesToSearch.filter(c => !c.comingSoon)

  if (courseId && courseId !== 'all') {
    const course = coursesToSearch.find(c => c.id === courseId)
    if (!course || course.comingSoon) return []
    return generateTeeTimes(date, course.id, course.name)
  }

  // When 'all' or no courseId, return tee times for all selectable courses in this club
  return selectableCourses.flatMap(course => generateTeeTimes(date, course.id, course.name))
}

// ─── Players ─────────────────────────────────────────────────────────────────

export const mockPrimaryPlayer: PlayerInfo = {
  id: 'player-1',
  firstName: 'James',
  lastName: 'Anderson',
  email: 'james.anderson@email.com',
  phone: '+61 412 345 678',
  title: 'Mr',
  handicap: '12',
  homeClubId: 'club-home-001',
  homeClub: {
    name: 'Kingston Heath Golf Club',
    country: 'Australia',
    state: 'Victoria',
    city: 'Cheltenham',
    address: '202 Kingston Rd, Cheltenham VIC 3192',
    website: 'https://www.kingstonheath.com.au',
    phone: '+61 3 9551 1255',
    email: 'info@kingstonheath.com.au',
  },
  isPrimary: true,
  bookingOptions: {
    caddie: false,
    clubHire: false,
    cart: false,
  },
  priceCents: 35000,
  isReciprocalMember: false,
  priceLabel: 'Visitor',
}

// ─── Price Breakdown ─────────────────────────────────────────────────────────

export interface MockPriceBreakdown {
  players: Array<{
    name: string
    label: string
    priceCents: number
  }>
  subtotalCents: number
  serviceFeeCents: number
  processingFeeCents: number
  totalCents: number
  currency: string
  currencySymbol: string
}

/** Service fee rate: 4% */
const SERVICE_FEE_RATE = 0.04

/** Stripe processing fee: 1.75% + 30c (Australian domestic cards) */
const STRIPE_RATE = 0.0175
const STRIPE_FIXED_CENTS = 30

export function getMockPriceBreakdown(players: PlayerInfo[], selectedTimeSlot?: TimeSlot | null): MockPriceBreakdown {
  const playerBreakdown = players.map(p => {
    // Determine price based on player's home club country
    let priceCents = p.priceCents || 35000
    let label = p.priceLabel || (p.isPrimary ? 'Primary' : 'Guest')

    if (selectedTimeSlot) {
      const isAustralian = p.homeClub?.country === 'Australia'
      priceCents = isAustralian ? selectedTimeSlot.domesticPrice : selectedTimeSlot.internationalPrice
      label = isAustralian ? 'Domestic' : 'International'
    }

    return {
      name: `${p.firstName} ${p.lastName}`,
      label,
      priceCents,
    }
  })

  const subtotalCents = playerBreakdown.reduce((sum, p) => sum + p.priceCents, 0)
  const serviceFeeCents = Math.round(subtotalCents * SERVICE_FEE_RATE)
  const processingFeeCents = Math.round((subtotalCents + serviceFeeCents) * STRIPE_RATE) + STRIPE_FIXED_CENTS

  return {
    players: playerBreakdown,
    subtotalCents,
    serviceFeeCents,
    processingFeeCents,
    totalCents: subtotalCents + serviceFeeCents + processingFeeCents,
    currency: 'AUD',
    currencySymbol: '$',
  }
}

// ─── Booking Confirmation ────────────────────────────────────────────────────

export const mockBookingConfirmation = {
  bookingId: 'bk-mock-12345',
  bookingReference: 'CG-RM-2026-0213',
  status: 'PENDING_VERIFICATION' as const,
  clubName: mockClub.name,
  courseName: 'West Course',
  teeTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  playerCount: 3,
  totalAmountCents: 115000 + 4025,
  currency: 'AUD',
  currencySymbol: '$',
}

// ─── Utility ─────────────────────────────────────────────────────────────────

export function formatPrice(amountCents: number, currencySymbol = '$'): string {
  return `${currencySymbol}${(amountCents / 100).toFixed(2)}`
}
